import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { Badge, Loading, Box, Heading, Text, Textarea, Button, Alert, Stack, FormControl, Input } from '@/components/lib'
import useTitle from '../hooks/useTitle'
import api from '../api'
import useStore from '../store'
import { buildComponentVisibilityMap, componentConditionIsMet } from '../utils/utils'

import Terms from './includes/Terms'
import RichTextViewer from './includes/RichTextViewer'
import PhoneInput from './includes/PhoneInput'

import ShortText from './fields/ShortText'
import LongText from './fields/LongText'
import Integer from './fields/Integer'
import Float from './fields/Float'
import Slider from './fields/Slider'
import Rating from './fields/Rating'
import Likert from './fields/Likert'
import TableField from './fields/Table'
import Choice from './fields/Choice'
import DateInput from './fields/Date'
import DatetimeInput from './fields/Datetime'
import TimeInput from './fields/Time'
import Temperature from './fields/Temperature'
import Height from './fields/Height'
import Weight from './fields/Weight'
import BMI from './fields/BMI'
import PromisForm from './fields/PromisForm'
import PromisBattery from './fields/PromisBattery'
import TeethChart from './fields/TeethChart'

export const POLL_FIELDS = {
  text: ShortText,
  paragraph: LongText,
  integer: Integer,
  float: Float,
  slider: Slider,
  rating: Rating,
  likert: Likert,
  choice: Choice,
  date: DateInput,
  datetime: DatetimeInput,
  time: TimeInput,
  temperature: Temperature,
  height: Height,
  weight: Weight,
  bmi: BMI,
  promisForm: PromisForm,
  promisBattery: PromisBattery,
  teethChart: TeethChart,
  table: TableField,
  calculation: null
}

export default function Poll () {
  const [trial, setTrial] = useState(null)
  const [stage, setStage] = useState(null)
  const [responses, setResponses] = useState({})
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [onScreenMessages, setOnScreenMessages] = useState([])
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [errorComponent, setErrorComponent] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { stageId, linkId } = useParams()
  const [searchParams] = useSearchParams()
  const linkToken = searchParams.get('token') || ''
  const { setTeam } = useStore()
  useTitle(stage ? stage.name : 'Poll')

  useEffect(() => {
    if (!stageId) setError('No poll could be found.')
    else {
      setLoading(true)
      api.stages.getPoll(stageId, data => {
        setStage(data)
        const team = data.ownerTeam
        if (data?.ownerTrial?.sponsor) team.name = data.ownerTrial.sponsor
        if (data?.ownerTrial?.sponsorLogoUrl) team.logoUrl = data.ownerTrial.sponsorLogoUrl
        setTrial(data.ownerTrial)
        setTeam(team)
        setLoading(false)
      }, err => {
        setError(`There was an error fetching the poll: ${err.message}`)
        setLoading(false)
      }, linkToken || undefined)
    }
  }, [stageId, linkToken])

  function submit () {
    if (!termsAgreed) return setError('You need to agree to the terms and conditions in order to submit your data.')
    setError()
    setErrorComponent()
    const responseData = {
      termsAgreed,
      response: responses,
      accessId: linkId
    }
    if (linkToken) {
      responseData.token = linkToken
    }
    if (stage.pollSettings?.requireEmail) {
      responseData.email = email
    }
    if (stage.pollSettings?.requirePhone) {
      responseData.phone = phone
    }
    setSubmitting(true)
    api.stages.submitPollData(stage._id, responseData, data => {
      // Clear any cached PROMIS form/battery progress so a fresh page load
      // after submitting starts a new questionnaire rather than showing the
      // previous respondent's completed state.
      try {
        Object.keys(window.sessionStorage)
          .filter(k => k.startsWith('promis_session_') || k.startsWith('promis_battery_'))
          .forEach(k => window.sessionStorage.removeItem(k))
      } catch { /* sessionStorage unavailable */ }
      setSuccess(true)
      setSubmitting(false)
      setPostSubmissionText(data.postSubmissionText)
      setOnScreenMessages(data.onScreenMessages || [])
    }, err => {
      let message = err.message || ''
      if (message?.indexOf('[') > -1) {
        const match = err.message.match(/\[([a-z0-9-]+)\]/i)
        setErrorComponent(match[1])
        message = message.replace(match[0], '')
      }
      setError(`There was an error submitting your response: ${message}`)
      setSubmitting(false)
    })
  }

  // Build component visibility map to handle chained conditionals
  // This processes components in order and accounts for dependencies (C depends on B depends on A)
  const componentVisibilityMap = useMemo(() => {
    return buildComponentVisibilityMap(stage?.components || [], responses)
  }, [stage?.components, responses])

  function conditionIsMet (c) {
    return componentConditionIsMet(c, componentVisibilityMap)
  }

  // 720px, not the 500px the other public pages use: a Likert renders as a grid
  // from the `md` breakpoint up, and its columns need roughly 9rem plus one per
  // scale point to sit side by side. At 500px that grid was wider than the page
  // and scrolled sideways. maxW is only a ceiling, so narrower viewports still
  // fill the screen, and below `md` the Likert stacks instead.
  return (
    <Stack gap={5} maxW='720px' mx='auto' p={5}>

      <Heading size='lg'>Welcome</Heading>
      {linkToken && stage?.participantId && (
        <Box p={3} borderWidth='1px' borderColor='border.default' bg='bg.canvas' rounded='md'>
          <Text fontSize='sm' color='fg.muted'>
            This form will submit data as participant{' '}
            <Text as='span' fontWeight='semibold' color='fg'>
              {stage.participantId}
            </Text>
            .
          </Text>
        </Box>
      )}
      {loading &&
        <Box display='flex' justifyContent='center'>
          <Loading />
        </Box>}

      {stage &&
        <>
          <Heading size='md'>{stage.name}</Heading>
          <Text color='fg.muted'>{stage.description}</Text>

          {success
            ? (
              <>
                <Alert status='success' title='Thank you'>
                  Your response has been submitted successfully.
                </Alert>
                {postSubmissionText && <RichTextViewer content={postSubmissionText} />}
                {onScreenMessages.map((message, i) => (
                  <RichTextViewer key={i} content={message} />
                ))}
              </>)
            : (
              <>
                <Stack spacing={5}>
                  {stage.components?.map(component => {
                    if (!conditionIsMet(component)) return null
                    if (component.type === 'title') {
                      return (
                        <Box key={component.id} p={2} bg='bg.accent'>
                          <Heading key={component.id} size='md'>{component.title}</Heading>
                        </Box>
                      )
                    }
                    if (component.type === 'sectionDivider') {
                      return (
                        <Box key={component.id} p={2} bg='bg.accent'>
                          <Heading size='md'>{component.title}</Heading>
                          <RichTextViewer content={component.description} />
                        </Box>
                      )
                    }
                    if (component.type?.type === 'calculation') return null
                    const Field = POLL_FIELDS[component.type?.type]
                    const value = responses[component.dataType]?.value
                    const onChange = v => setResponses({
                      ...responses,
                      [component.dataType]: {
                        value: v,
                        createdAt: new Date()
                      }
                    })
                    return (
                      <Stack gap={3} key={component.id} p={3} borderWidth='1px' rounded='md' bg={errorComponent === component.id ? 'red.100' : 'bg.surface'}>
                        {component.isRequired &&
                          <Box display='flex' justifyContent='flex-end'>
                            <Badge colorPalette='red'>Required</Badge>
                          </Box>}

                        <Heading as='h3' size='md'>{component.title}</Heading>
                        <RichTextViewer content={component.description} />
                        <Box>
                          {Field
                            ? <Field dataType={component.type} stageId={stageId} value={value} onChange={onChange} />
                            : <Alert variant='left-accent'>This question type is not yet supported in polls.</Alert>}
                        </Box>
                      </Stack>
                    )
                  })}
                </Stack>

                {(stage.pollSettings?.requireEmail || stage.pollSettings?.requirePhone) &&
                  <Stack gap={3} p={3} bg='bg.accentSubtle' rounded='md'>
                    <Box>
                      <Heading size='md'>Contact details</Heading>
                      <Text>In order to submit your response, we need to collect your contact details.</Text>
                    </Box>

                    {stage.pollSettings?.requireEmail &&
                      <FormControl label='Email address' isRequired>
                        <Input bg='bg.surface' type='email' value={email} onChange={e => setEmail(e.target.value)} placeholder='jane@exmaple.com' />
                      </FormControl>}

                    {stage.pollSettings?.requirePhone &&
                      <FormControl label='Phone number' isRequired>
                        <PhoneInput
                          bg='bg.surface'
                          value={phone}
                          onChange={v => setPhone(v)}
                          placeholder='Enter your phone number'
                        />
                      </FormControl>}
                  </Stack>}

                <Stack gap={3} p={3} bg='bg.subtle' rounded='md'>
                  <Heading size='md'>Conditions & privacy</Heading>

                  {trial?.conditions && (
                    <>
                      <Textarea readOnly bg='bg.surface' rows={8} value={trial.conditions} />
                    </>
                  )}

                  <Terms value={termsAgreed} onChange={setTermsAgreed} agreementStatement='I have read and I agree to the trial conditions. I have also read and I agree to the Trialflare conditions and privacy statement' />
                </Stack>

                {error && <Alert status='error'>{error}</Alert>}
                <Button w='100%' colorPalette='primary' onClick={submit} isLoading={submitting}>Submit</Button>
              </>)}
        </>}

    </Stack>
  )
};
